import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { SortDownArrow } from "../../assets/svg";
import Action from "../../components/common/Action";
import Breadcrumb from "../../components/common/BreadCrumb";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customTable/CustomTable";
import RippleLoader from "../../components/loader/RippleLoader";
import { Badge, DefaultPrimaryButton } from "../../components/micro/input";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import { Close } from "@mui/icons-material";
import {
  deleteProductInterest,
  getProductInterestList,
  updateProductInterest,
} from "../../redux/product/slice";
import { getSalesPersonList } from "../../redux/companies/slice";

const ProductInterest = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const selectedTab = location?.state?.tab || "all";
  const [searchValue, setSearchValue] = useState("");
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(selectedTab);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [salesPersonFilter, setSalesPersonFilter] = useState(null);
  const [popupData, setPopupData] = useState({ id: null, status: "" });
  const [confirmModal, setConfirmModal] = useState(false);

  // selector
  const { productInterestList, productInterestListLoading, salesPersonList } =
    useSelector((store) => ({
      productInterestList:
        store?.productsData?.getProductInterestListStatus?.data,
      productInterestListLoading: store?.productsData?.loading,
      salesPersonList: store?.companiesData?.getSalesPersonListStatus?.data,
    }));

  useEffect(() => {
    dispatch(
      getProductInterestList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        category_id: "",
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        status: statusFilter?.value ?? activeTab,
        sales_person: salesPersonFilter?.value || "",
      })
    );
    dispatch(getSalesPersonList({ search: "", limit: "all" }));
  }, [
    searchValue,
    pageCount,
    pageLimit,
    sortBy,
    sortDirection,
    statusFilter,
    activeTab,
    salesPersonFilter,
  ]);

  const tabs = [
    { label: "All", value: "all" },
    { label: "Pending", value: "pending" },
    { label: "In Talk", value: "in_talk" },
    { label: "Closed", value: "closed" },
    { label: "Rejected", value: "rejected" },
  ];

  const handleSalesExportClick = () => {
    const headers = [
      "No.",
      "Company",
      "Outlet",
      "Sales Person",
      "Item Qty",
      "Date",
      "Status",
      "Amount",
    ];

    const rows = productInterestList?.data?.data?.map((item) => [
      item?.id,
      item?.request_details?.company_name,
      item?.request_details?.outlet_name,
      item?.user_name,
      item?.total_request,
      item?.date,
      item?.status,
      item?.amount,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((e) => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "product_interest.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ColumnHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        No.
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("outlet");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Outlet</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "outlet" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Sales Person
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Item Quantity
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Date</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("status");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Status</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "status" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("amount");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Amount</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "amount" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Actions
      </th>
    </>
  );

  const filteredData = (productInterestList?.data?.data || []).filter(
    (item) => {
      const tabMatch =
        activeTab === "all" ||
        item?.status.toLowerCase() === activeTab.toLowerCase();

      const dropdownMatch =
        !statusFilter || item?.status === statusFilter.value;

      const salesMatch =
        !salesPersonFilter || item?.user_id === salesPersonFilter.value;

      return tabMatch && dropdownMatch && salesMatch;
    }
  );

  const salesPersonOptions = salesPersonList?.data?.map((salesPerson) => ({
    label: salesPerson?.full_name,
    value: salesPerson?.id,
  }));

  const updateStatus = () => {
    setIsLoading(true);

    if (popupData?.status === "delete") {
      dispatch(deleteProductInterest(popupData?.id)).then(() => {
        dispatch(
          getProductInterestList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            category_id: "",
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
            status: statusFilter?.value ?? activeTab,
            sales_person: salesPersonFilter?.value || "",
          })
        );
        setIsLoading(false);
        setConfirmModal(false);
      });
    } else {
      dispatch(
        updateProductInterest({
          id: popupData?.id,
          data: { status: popupData?.status },
        })
      ).then((res) => {
        if (res.type === "updateProductInterest/fulfilled") {
          dispatch(
            getProductInterestList({
              search: searchValue,
              pageCount: pageCount,
              pageLimit: pageLimit,
              category_id: "",
              sort_by: sortBy,
              sort_direction: sortDirection === "asc" ? "asc" : "desc",
              status: statusFilter?.value ?? activeTab,
              sales_person: salesPersonFilter?.value || "",
            })
          );
        }
        setIsLoading(false);
        setConfirmModal(false);
      });
    }
  };

  const DataRows = () => (
    <>
      {filteredData?.map((item, index) => (
        <tr
          key={index}
          className="font-Proxima shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-4 py-3">{item?.id}</td>
          <td className="px-4 py-3">
            {item?.request_details?.company_name ?? "-"}
          </td>
          <td className="px-4 py-3">
            {item?.request_details?.outlet_name ?? "-"}
          </td>
          <td className="px-4 py-3">{item?.user_name ?? "-"}</td>
          <td
            className="px-4 py-3 text-themeBtn font-semibold cursor-pointer underline"
            onClick={() => {
              setSelectedRowId(item);
              setShowBreakdown(true);
            }}
          >
            {item?.total_request ?? "-"}
          </td>
          <td className="px-4 py-3">
            {" "}
            {item?.date
              ? new Date(item?.date).toLocaleDateString("en-GB")
              : "-"}
          </td>
          <td className="px-4 py-3">
            <div className="flex justify-start ">
              <Badge
                className={`capitalize whitespace-nowrap ${
                  item?.status === "in_talk"
                    ? "border-lightBlue text-chipBlue"
                    : item?.status === "pending"
                    ? "border-stepGray text-primaryBlack"
                    : item?.status === "closed"
                    ? "border-mediumGreen text-bgGreen"
                    : item?.status === "rejected"
                    ? "border-lightRed text-red"
                    : ""
                }`}
                value={
                  item?.status === "in_talk"
                    ? "In Talk"
                    : item?.status === "pending"
                    ? "Pending"
                    : item?.status === "closed"
                    ? "Closed"
                    : item?.status === "rejected"
                    ? "Rejected"
                    : item?.status == null
                    ? "-"
                    : "-"
                }
              />
            </div>
          </td>
          <td className="px-4 py-3">
            $
            {item?.amount?.toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })}
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  {item?.status === "pending" && (
                    <>
                      <li
                        className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                        onClick={() => {
                          setConfirmModal(true);
                          setPopupData({ id: item?.id, status: "in_talk" });
                        }}
                      >
                        In-Talk
                      </li>
                      <li
                        className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                        onClick={() => {
                          setConfirmModal(true);
                          setPopupData({ id: item?.id, status: "closed" });
                        }}
                      >
                        Closed
                      </li>
                      <li
                        className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                        onClick={() => {
                          setConfirmModal(true);
                          setPopupData({ id: item?.id, status: "rejected" });
                        }}
                      >
                        Rejected
                      </li>
                    </>
                  )}

                  {item?.status === "in_talk" && (
                    <>
                      <li
                        className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                        onClick={() => {
                          setConfirmModal(true);
                          setPopupData({ id: item?.id, status: "closed" });
                        }}
                      >
                        Closed
                      </li>
                      <li
                        className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                        onClick={() => {
                          setConfirmModal(true);
                          setPopupData({ id: item?.id, status: "rejected" });
                        }}
                      >
                        Rejected
                      </li>
                    </>
                  )}

                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() => {
                      setConfirmModal(true);
                      setPopupData({ id: item?.id, status: "delete" });
                    }}
                  >
                    Delete
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  const ItemsBreakdownModal = ({ show, onClose, selectedId }) => {
    if (!show) return null;

    const breakdownItems = [selectedId] || [];

    return (
      <div className="backdrop-blur-sm bg-black/40 flex items-center justify-center w-full fixed top-0 left-0 right-0 z-[9999] mx-auto h-screen !my-0">
        <div
          className={`bg-white flex flex-col justify-center drop-shadow-lg rounded-lg w-full max-w-4xl py-8 px-6 mx-auto relative`}
        >
          <div className="flex justify-between mb-4">
            <h4 className="text-3xl font-Montserrat font-light">
              Items Breakdown
            </h4>
            <button onClick={onClose} className="font-light">
              <Close />
            </button>
          </div>
          {breakdownItems.map((item, index) => (
            <div
              key={index}
              className="mt-2 text-gray-600 space-y-1 mb-5 font-Proxima text-sm flex gap-10"
            >
              <div className="flex flex-col">
                <span>
                  Company: {item?.request_details?.company_name || "-"}
                </span>
                <span>Outlet: {item?.request_details?.outlet_name || "-"}</span>
                <span>
                  Date of Request:{" "}
                  {item?.request_details?.date_of_request
                    ? new Date(
                        item?.request_details?.date_of_request
                      ).toLocaleDateString("en-GB")
                    : "-"}
                </span>
              </div>
              <div className="flex flex-col">
                <span>
                  Contact Number: {item?.request_details?.contact_number || "-"}
                </span>
                <span>
                  Contact Person:{" "}
                  {item?.request_details?.contact_person_name || "-"}
                </span>
                <span>Email: {item?.request_details?.email || "-"}</span>
              </div>
            </div>
          ))}
          <table className="w-full text-sm table-fixed">
            <thead className="text-sm text-primaryBlack bg-bgLightTheme font-normal text-start">
              <tr className="bg-gray-100 text-sm text-left">
                <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                  No.
                </th>
                <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                  Item
                </th>
                <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                  Category
                </th>
                <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                  Quantity
                </th>
                <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="text-sm font-normal">
              {breakdownItems.map((item, index) => (
                <tr
                  key={index}
                  className="font-Proxima shadow-sm text-mgrey hover:bg-lightprimaryorang"
                >
                  <td className="px-4 py-2">{index + 1}</td>
                  <td className="px-4 py-2">
                    {item?.request_details?.product_name ?? "-"}
                  </td>
                  <td className="px-4 py-2">
                    {item?.request_details?.category_name ?? "-"}
                  </td>
                  <td className="px-4 py-2">
                    {item?.request_details?.quantity ?? "-"}
                  </td>
                  <td className="px-4 py-2">
                    $
                    {item?.request_details?.amount?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-7">
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              Product Interest
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Product Interest"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
        </div>
        <div className="flex justify-end mt-8">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              className={`py-2 w-full font-normal mb-5 h-full px-4 uppercase text-sm border-b-2 transition duration-300 font-Proxima ${
                activeTab === tab.value
                  ? "border-themeBtn text-themeBtn"
                  : "border-transparent text-textSecondary hover:text-themeBtn hover:border-theme text-gray-dark"
              }`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-3">
          <ReactSelect
            className="w-full max-w-52 text-black p-0 font-Proxima"
            id="status"
            placeholder="Status"
            options={tabs}
            isClearable={true}
            getOptionValue={(option) => option?.value}
            getOptionLabel={(option) => option?.label}
            value={statusFilter}
            onChange={(selectedOption) => setStatusFilter(selectedOption)}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: statusFilter ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />

          <ReactSelect
            className="w-full max-w-52 text-black p-0 font-Proxima"
            id="sales"
            placeholder="Sales Person"
            options={salesPersonOptions}
            isClearable={true}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            value={salesPersonFilter}
            onChange={(selectedOption) => setSalesPersonFilter(selectedOption)}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />

          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
          <DefaultPrimaryButton
            value="EXPORT"
            onClick={handleSalesExportClick}
          />
        </div>
        {productInterestListLoading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg">
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={productInterestList?.data}
              overflowVisibleLg={true}
              // loader
              loading={false}
              showPagination={true}
              // showLimit
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              // paginationData
              total={productInterestList?.data?.length}
              current={pageCount}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(
                productInterestList?.data?.length / pageLimit
              )}
            />
          </div>
        )}
      </div>
      {confirmModal && (
        <div>
          <ConfirmationModel
            confirmText={`Change Status to ${popupData.status}`}
            onConfirm={updateStatus}
            onClose={() => setConfirmModal(false)}
            isLoading={isLoading}
          />
        </div>
      )}
      <ItemsBreakdownModal
        show={showBreakdown}
        selectedId={selectedRowId}
        onClose={() => setShowBreakdown(false)}
      />
    </>
  );
};

export default ProductInterest;
